// src/components/AppInsightsErrorBoundary/AppInsightsErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from '../../appinsights';

interface Props {
    children: ReactNode;
    /** UI alternativa a mostrar cuando ocurre un error. Opcional. */
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * ErrorBoundary que reporta excepciones no controladas a Application Insights.
 *
 * Colocar alrededor de secciones críticas de la app (p.ej. AppRoutes en App.tsx).
 * Para errores manejados manualmente, usar useTrackEvent().trackException(error).
 *
 * Uso:
 *   <AppInsightsErrorBoundary>
 *     <ComponenteQuePuedeRomper />
 *   </AppInsightsErrorBoundary>
 *
 *   // Con UI custom de fallback:
 *   <AppInsightsErrorBoundary fallback={<PaginaDeError />}>
 *     ...
 *   </AppInsightsErrorBoundary>
 */
class AppInsightsErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        if (appInsights) {
            appInsights.trackException({
                exception: error,
                severityLevel: SeverityLevel.Critical,
                properties: {
                    componentStack: errorInfo.componentStack ?? '',
                    source: 'AppInsightsErrorBoundary',
                },
            });
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
        }

        return this.props.children;
    }
}

export default AppInsightsErrorBoundary;
